import { useTranslation } from 'react-i18next';
import type { CreateLogRequestFormularySchema } from './hooks/useFormularyOfCreateLogRequest.hook';
import { useFormularyOfCreateLogRequests } from './hooks/useFormularyOfCreateLogRequest.hook';
import { useState, useCallback, useMemo } from 'react';
import { LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { useDeviceManagementError } from '../../hooks';
import { useBusinessService } from '@ingenico/react-section-utils';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { requestAndUpdateState, useAsyncDataState } from '@ingenico/react-utils';
import type { CoreError } from '@ingenico/api-sdk-core';
import { Level, showNotification } from '@ingenico/react-notifications';
import { isEmpty, toString } from 'lodash-es';

export interface UseCreateLogRequestModalVMOutput {
  readonly form: ReturnType<typeof useFormularyOfCreateLogRequests>;
  readonly errorMessage: string;
  readonly logRequestState: ReturnType<typeof useAsyncDataState<unknown, CoreError>>;
  readonly handleCancel: () => void;
  readonly onCreateLogSubmission: () => Promise<void>;
  readonly isError: boolean;
}

export interface UseCreateLogRequestModalVMParams {
  readonly logicalDeviceId: string;
  readonly onSubmit?: (input: CreateLogRequestFormularySchema) => Promise<void> | void;
  readonly onCancel?: () => void;
  readonly isOpen: boolean;
  readonly errorMessage?: string;
}

export function useCreateLogRequestModalVM({
  logicalDeviceId,
  onCancel,
  onSubmit,
  isOpen,
  errorMessage: errorMessageProp,
}: UseCreateLogRequestModalVMParams) {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));

  const form = useFormularyOfCreateLogRequests({ isOpen });

  const [errorMessage, setErrorMessage] = useState('');

  const { getErrorMessage } = useDeviceManagementError();
  const provisioningBusinessService = useBusinessService(DeviceManagementService);

  const [logRequestState, setLogRequestState] = useAsyncDataState<unknown, CoreError>();

  const handleCancel = useCallback(() => {
    onCancel?.();
    setErrorMessage('');
    setLogRequestState({
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
      data: null,
    });
  }, [onCancel, setLogRequestState]);

  const onCreateLogRequest = useCallback(
    (input: CreateLogRequestFormularySchema): Promise<void> => {
      return new Promise((resolve, reject) => {
        requestAndUpdateState(
          provisioningBusinessService.createLogRequestFromFormulary(logicalDeviceId, input),
          setLogRequestState,
          {
            onSuccess: () => {
              showNotification({
                content: toString(t('logcat.creation.success')),
                level: Level.Success,
              });
              resolve(void 0);
            },
            onError: (error) => {
              showNotification({ content: getErrorMessage(error), level: Level.Error });
              reject(error);
            },
          },
        );
      }).then(() => onSubmit?.(input));
    },
    [getErrorMessage, logicalDeviceId, onSubmit, provisioningBusinessService, setLogRequestState, t],
  );

  const onCreateLogSubmission = useCallback(async () => {
    await form.handleSubmit((formulary) => onCreateLogRequest(formulary))();
  }, [form, onCreateLogRequest]);

  const isError = useMemo(() => !isEmpty(errorMessage) || !isEmpty(errorMessageProp), [errorMessage, errorMessageProp]);

  return {
    form,
    errorMessage,
    logRequestState,
    handleCancel,
    onCreateLogSubmission,
    isError,
  };
}
